import type { Node, NodeGroup } from '../types';
import { GROUP } from '../constants/groups';

/** Get all descendant node IDs of a group recursively (through nested sub-groups). */
export function getDescendantNodeIds(groups: ReadonlyArray<NodeGroup>, groupId: string): string[] {
  const group = groups.find((g) => g.id === groupId);
  if (!group) return [];
  const result = [...group.childNodeIds];
  group.childGroupIds.forEach((cgid) => {
    result.push(...getDescendantNodeIds(groups, cgid));
  });
  return result;
}

/** Get all ancestor group IDs walking up `parentGroupId` from `groupId`. */
export function getAncestorGroupIds(groups: ReadonlyArray<NodeGroup>, groupId: string): string[] {
  const ids: string[] = [];
  let current = groups.find((g) => g.id === groupId);
  while (current?.parentGroupId) {
    ids.push(current.parentGroupId);
    current = groups.find((g) => g.id === current!.parentGroupId);
  }
  return ids;
}

/**
 * Recalculate the bounding box of `groupId` from its child nodes + sub-groups.
 * Mutates the matching group entry in `groups` (use within an Immer/Zustand draft).
 *
 * Skips frames (`isFrame`) and collapsed groups — they keep their drawn size.
 */
export function recalcGroupBounds(
  groups: NodeGroup[],
  nodes: ReadonlyArray<Node>,
  groupId: string,
): void {
  const group = groups.find((g) => g.id === groupId);
  if (!group || group.collapsed || group.isFrame) return;

  const PADDING = GROUP.PADDING;
  const HEADER = GROUP.HEADER;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  let hasContent = false;

  group.childNodeIds.forEach((nid) => {
    const n = nodes.find((node) => node.id === nid);
    if (!n) return;
    hasContent = true;
    minX = Math.min(minX, n.position.x);
    minY = Math.min(minY, n.position.y);
    maxX = Math.max(maxX, n.position.x + n.size.width);
    maxY = Math.max(maxY, n.position.y + n.size.height);
  });

  group.childGroupIds.forEach((gid) => {
    const sg = groups.find((g) => g.id === gid);
    if (!sg) return;
    hasContent = true;
    minX = Math.min(minX, sg.position.x);
    minY = Math.min(minY, sg.position.y);
    maxX = Math.max(maxX, sg.position.x + sg.size.width);
    maxY = Math.max(maxY, sg.position.y + sg.size.height);
  });

  if (!hasContent) return;
  group.position = { x: minX - PADDING, y: minY - PADDING - HEADER };
  group.size = { width: maxX - minX + PADDING * 2, height: maxY - minY + PADDING * 2 + HEADER };
}
